import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Heart, Sparkles, Instagram, MapPin } from "lucide-react";
import { api, gbp, assetUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import BeholdFeed from "@/components/BeholdFeed";

const Row = ({ title, subtitle, link, children, testid }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12" data-testid={testid}>
    <div className="flex items-end justify-between mb-7">
      <div>
        {subtitle && <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">{subtitle}</p>}
        <h2 className="font-serif-display text-3xl sm:text-4xl">{title}</h2>
      </div>
      {link && <Link to={link} className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-luxe hover:text-brand-pink transition-colors">View all <ArrowRight className="w-4 h-4" /></Link>}
    </div>
    {children}
  </section>
);

export default function Home() {
  const [hp, setHp] = useState({});
  const [hpLoaded, setHpLoaded] = useState(false);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [best, setBest] = useState([]);
  const [news, setNews] = useState([]);
  const [seasonal, setSeasonal] = useState([]);
  const [bundles] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/content/homepage").then((r) => setHp(r.data?.data || {})).finally(() => setHpLoaded(true));
    api.get("/shopify/collections").then((r) => {
      const cols = r.data || [];
      const order = ["Bobble Hats", "Beanies", "Winter Bucket Hats", "Headbands", "Scarves"];
      const ordered = order.map((n) => cols.find((c) => c.name.trim().toLowerCase() === n.toLowerCase())).filter(Boolean);
      setCats(ordered.length ? ordered : cols.filter((c) => !/default example/i.test(c.name)).slice(0, 4));
    });
    api.get("/shopify/products").then((r) => {
      const all = r.data;
      setFeatured(all.slice(0, 4));
      setNews(all.slice(4, 8));
    });
    api.get("/shopify/bestsellers?limit=4").then((r) => setBest(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3)));
  }, []);

  return (
    <div>
      <Seo title="Handmade Crochet Gifts & Accessories" description={hp.hero_subtitle} image={assetUrl(hp.hero_image)} />

      {/* Hero */}
      <section className="relative h-[78vh] min-h-[520px] flex items-center bg-brand-blush/40" data-testid="hero">
        {hp.hero_image && <img src={assetUrl(hp.hero_image)} alt="Handmade crochet" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {hpLoaded && (
          <div className="max-w-xl ck-fade-up">
            <p className="text-xs uppercase tracking-luxe text-brand-ink/70 mb-4">{hp.hero_eyebrow || "Handmade in the UK · Stitch by stitch"}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">{hp.hero_title || "Handmade with love"}</h1>
            <p className="text-base sm:text-lg text-brand-ink/80 mb-8 font-light">{hp.hero_subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="bg-brand-ink text-white px-8 py-3.5 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="hero-cta">{hp.hero_cta || "Shop Collections"}</Link>
            </div>
          </div>
          )}
        </div>
      </section>

      {/* trust strip */}
      <div className="bg-brand-blush/50 border-y border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center text-sm">
          <div className="flex items-center justify-center gap-2"><Truck className="w-4 h-4 text-brand-pink" /> Free UK shipping over £40</div>
          <div className="flex items-center justify-center gap-2"><Heart className="w-4 h-4 text-brand-pink" /> Lovingly handmade to order</div>
          <div className="flex items-center justify-center gap-2"><Sparkles className="w-4 h-4 text-brand-pink" /> Premium yarns & gift-ready</div>
        </div>
      </div>

      {/* Where to find me in person (editable in admin, hidden when empty) */}
      {hp.events_text && (
        <section className="bg-brand-blush/50 border-b border-brand-border" data-testid="in-person-block">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-11 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/70 mb-4">
              <MapPin className="w-5 h-5 text-brand-pink" />
            </div>
            <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">Where to find me</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl mb-4">{hp.events_title || "Find me in person"}</h2>
            <p className="text-base text-brand-ink/80 whitespace-pre-line leading-relaxed" data-testid="in-person-text">{hp.events_text}</p>
          </div>
        </section>
      )}

      {/* Featured collections */}
      <Row title="Collections" subtitle="Shop by category" testid="featured-collections">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {cats.map((c) => (
            <Link key={c.id} to={`/shop?collection=${c.slug}`} className="group relative h-72 overflow-hidden rounded-sm" data-testid={`collection-${c.slug}`}>
              {c.image
                ? <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                : <div className="w-full h-full bg-gradient-to-br from-brand-blush via-brand-pink/60 to-brand-blush transition-transform duration-700 group-hover:scale-105" />}
              <div className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" />
              <div className="absolute bottom-0 p-6 text-white">
                <h3 className="font-serif-display text-2xl mb-1">{c.name}</h3>
                <p className="text-sm text-white/85 max-w-xs">{c.description}</p>
                <span className="inline-flex items-center gap-2 mt-3 text-xs uppercase tracking-luxe">Explore <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Row>

      {best.length > 0 && (
        <div className="bg-brand-blush/30">
          <Row title="Bestsellers" subtitle="Loved by customers" link="/shop?bestseller=true" testid="bestsellers">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{best.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
          </Row>
        </div>
      )}

      {news.length > 0 && (
        <Row title="New arrivals" subtitle="Fresh off the hook" link="/shop?new_arrival=true" testid="new-arrivals">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{news.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </Row>
      )}

      {/* Bundle offers */}
      {seasonal.length > 0 && (
        <Row title="Seasonal favourites" subtitle="In season now" link="/shop?seasonal=true" testid="seasonal">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{seasonal.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
        </Row>
      )}

      {/* Lookbook */}
      {(hp.lookbook_images || []).length > 0 && (
        <Row title={hp.lookbook_title || "The lookbook"} subtitle="Styled & worn" testid="lookbook">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {(hp.lookbook_images || []).map((img, i) => (
              <div key={i} className="aspect-[3/4] overflow-hidden rounded-sm group" data-testid={`lookbook-${i}`}>
                <img src={assetUrl(img)} alt="Crochet Kerry styled" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            ))}
          </div>
        </Row>
      )}

      {/* About the maker */}
      <section className="bg-brand-blush/40" data-testid="about-maker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="h-[420px] overflow-hidden rounded-sm"><img src={assetUrl(hp.maker_image)} alt="The maker at work" className="w-full h-full object-cover" /></div>
          <div>
            <p className="text-xs uppercase tracking-luxe text-brand-pink mb-3">Meet the maker</p>
            <h2 className="font-serif-display text-4xl mb-5">{hp.maker_title || "Why Crochet Kerry?"}</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">{hp.maker_text}</p>
            <Link to="/about" className="inline-flex items-center gap-2 text-sm uppercase tracking-luxe hover:text-brand-pink transition-colors">Read our story <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      {reviews.length > 0 && (
        <Row title="Kind words" subtitle="Customer reviews" testid="reviews">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <div key={r.id} className="border border-brand-border rounded-sm p-7 bg-white" data-testid={`review-${r.id}`}>
                <div className="text-brand-pink mb-3">{"★".repeat(r.rating)}<span className="text-brand-border">{"★".repeat(5 - r.rating)}</span></div>
                <h4 className="font-serif-display text-xl mb-2">{r.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">"{r.body}"</p>
                <p className="text-xs uppercase tracking-luxe">— {r.author}</p>
              </div>
            ))}
          </div>
        </Row>
      )}

      {/* Instagram */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="instagram-feed">
        <div className="text-center mb-7">
          <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">Follow along</p>
          <h2 className="font-serif-display text-4xl inline-flex items-center gap-3"><Instagram className="w-7 h-7" /> {hp.instagram_handle || "@crochetkerry"}</h2>
        </div>
        <div data-testid="instagram-widget">
          <BeholdFeed />
        </div>
      </section>
    </div>
  );
}
